import { useQuery } from '@apollo/client';
import { Stack } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ApprovalBranchViewRowValues, ApprovalValues } from '../../../types';

import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { movingNodeId } from '../../ApproverLevels';
import { convertToApprovalLevelNodes } from '../../ApproverLevels/utils';
import { setNodeScrollState } from '../../Conditions';
import { Add } from '../common/Add';
import { FormView } from './FormView';
import APPROVAL_TYPE_DATA from '../../../../ApprovalSetupFlowCommonData.graphql';

// @ts-ignore
export const AddApprovalBranchNode: React.FC = ({ data, isConnectable }) => {
  const calloutId = useId(`AddLevelNode-callOutId`);
  const [visibility, setVisibility] = useState(false);
  const formMethods = useFormContext<ApprovalValues>();
  const { setNodes, setEdges } = useReactFlow();

  const { data: approvalTypes } = useQuery<ApprovalSetupFlowCommonData>(
    APPROVAL_TYPE_DATA,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const approvalTrees = formMethods.watch('approvalTrees') || [];

  const defaultBranchName = approvalTrees[data.levelIndex]?.name;
  const handleAddCondition = (fields: ApprovalBranchViewRowValues) => {
    const branches = [
      ...(approvalTrees[data.levelIndex].approvalBranches || []),
    ];

    const [emptyBranch, existingBranches] = branches.reduce(
      (
        acc: [
          ApprovalBranchViewRowValues | null,
          ApprovalBranchViewRowValues[]
        ],
        obj: ApprovalBranchViewRowValues
      ) => {
        if (Object.values(obj).every((value) => value === null)) {
          acc[0] = obj; // the all-null branch
        } else {
          acc[1].push(obj); // remaining branch
        }
        return acc;
      },
      [null, []]
    );
    const lastBranchOrder = branches[branches.length - 2]?.branchOrder ?? 0;
    const updatedBranches = [
      ...existingBranches,
      {
        ...fields,
        branchOrder: lastBranchOrder + 1,
      },
      emptyBranch,
    ];

    const treeUpdated = {
      ...approvalTrees[data.levelIndex],
      approvalBranches: updatedBranches,
    };

    // @ts-ignore
    formMethods.setValue(`approvalTrees.${data.levelIndex}`, treeUpdated, {
      shouldDirty: true,
    });

    setTimeout(() => {
      const { nodes: modulatedNodes, edges: modulatedEdges } =
        convertToApprovalLevelNodes(
          // @ts-ignore
          formMethods.getValues('approvalTrees'),
          'vertical'
        );
      setNodes([...modulatedNodes]);
      setEdges([...modulatedEdges]);
    }, 100);

    setNodeScrollState(true);
    setVisibility(false); // optionally close the Callout
  };
  const movingNode = movingNodeId();

  return (
    <Stack style={{ opacity: movingNode && movingNode !== data.id ? 0.5 : 1 }}>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
      />
      <Add
        title="Add Branch"
        id={calloutId}
        onClick={() => {
          setVisibility(true);
          setNodeScrollState(false);
        }}
      />

      <FormView
        onSave={handleAddCondition}
        approvalTypes={approvalTypes}
        onClose={() => {
          setNodeScrollState(true);
          setVisibility(false);
        }}
        visibility={visibility}
        defaultBranchName={defaultBranchName}
      />
    </Stack>
  );
};
